// TV mode (audit 2026-10, H7c): METRICS_VIEWER is the read-only role used on office screens/TVs.
// There the metric pages drop every control (period select, help menu, tour, retry buttons),
// refresh themselves (see auto-refresh.ts), say when the data is from and use larger type.
// It is ON for users whose only role is METRICS_VIEWER and opt-in for anyone with `?modo=tv`, so
// an ADMIN can preview what the screen shows.
import {
  ChangeDetectionStrategy,
  Component,
  type Signal,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, of, take } from 'rxjs';

import { AuthService } from '../core/auth/auth.service';
import { OPS_ROLES, ROLES, type Role } from '../core/auth/role-tiers';
import { createArgentinaDateTimeFormat } from './argentina-datetime-format';

export const TV_MODE_QUERY_PARAM = 'modo';
export const TV_MODE_QUERY_VALUE = 'tv';

/** TV mode for these roles and `?modo=` value (pure, so the rule is testable on its own). */
export function isTvMode(hasRole: (role: Role) => boolean, modo: string | null): boolean {
  if (modo === TV_MODE_QUERY_VALUE) {
    return true;
  }
  return hasRole(ROLES.METRICS_VIEWER) && !OPS_ROLES.some((role) => hasRole(role));
}

/** TV mode of the current page. Call in an injection context (a component field). */
export function injectTvMode(): Signal<boolean> {
  const auth = inject(AuthService);
  // Optional: a page rendered outside the router (unit tests, stories) falls back to the role.
  const route = inject(ActivatedRoute, { optional: true });
  const modo = toSignal(
    route ? route.queryParamMap.pipe(map((params) => params.get(TV_MODE_QUERY_PARAM))) : of(null),
    { initialValue: null },
  );
  return computed(() => isTvMode((role) => auth.hasRole(role), modo()));
}

/**
 * TV mode for an app-wide (root) service: null while it is not known yet. On a fresh load the
 * session identity can arrive before the first navigation ends (the auth guard waits on it), when
 * `?modo=tv` is not in the root route yet; a service must not start anything in that window.
 */
export function injectAppTvMode(): Signal<boolean | null> {
  const tvMode = injectTvMode();
  // No route config (a unit test without the router): nothing to wait for.
  const router = inject(Router, { optional: true });
  if (!router || router.config.length === 0) {
    return tvMode;
  }
  const navigated = toSignal(
    router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      take(1),
      map(() => true),
    ),
    { initialValue: router.navigated },
  );
  return computed(() => (navigated() ? tvMode() : null));
}

/**
 * TV type scale: bound as the page host `[style]` while TV mode is on, so every descendant that
 * reads the design tokens (page, kpi-card, chart wrapper) grows together, with no duplicated
 * template. Smallest text is 15px (`--font-size-xs`), above the 14px floor for a 1920×1080 screen
 * read from a distance. The `--kpi-*` properties are read by kpi-card with its normal values as
 * fallback.
 */
export const TV_TYPE_SCALE: Readonly<Record<string, string>> = {
  '--font-size-xs': '0.9375rem',
  '--font-size-sm': '1rem',
  '--font-size-md': '1.0625rem',
  '--font-size-lg': '1.25rem',
  '--font-size-xl': '1.75rem',
  '--font-size-2xl': '2.5rem',
  '--kpi-value-size': 'clamp(2rem, 1.25rem + 7cqw, 4.5rem)',
  '--kpi-compact-value-size': 'clamp(1.5rem, 1rem + 4cqw, 2.25rem)',
  '--kpi-caption-size': '0.9375rem',
  '--tv-status-size': '1.125rem',
};

const TIME_FORMAT = createArgentinaDateTimeFormat({
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** `hh:mm` in Argentina time, whatever the zone of the screen's browser. */
export function formatArgentinaTime(at: Date): string {
  return TIME_FORMAT.format(at);
}

/**
 * "Actualizado hh:mm", or why the screen shows older data. A TV has nobody to press "Reintentar":
 * the next automatic refresh retries, so this line is the only error affordance.
 */
@Component({
  selector: 'app-tv-refresh-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<p class="tv-status" [class.tv-status--failed]="failed()" role="status">
    {{ text() }}
  </p>`,
  styles: `
    :host {
      display: block;
    }

    .tv-status {
      margin: 0;
      font-size: var(--tv-status-size, var(--font-size-md));
      color: var(--color-text-secondary);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    .tv-status--failed {
      color: var(--color-warning-text-strong);
      font-weight: 600;
    }
  `,
})
export class TvRefreshStatus {
  /** Time of the oldest data on screen that was loaded successfully; null before the first load. */
  readonly updatedAt = input<Date | null>(null);
  /** The last refresh failed (the page keeps showing its previous data). */
  readonly failed = input(false);

  protected readonly text = computed(() => {
    const at = this.updatedAt();
    if (this.failed()) {
      return at
        ? `No se pudo actualizar — mostrando datos de ${formatArgentinaTime(at)}`
        : 'No se pudo actualizar';
    }
    return at ? `Actualizado ${formatArgentinaTime(at)}` : '';
  });
}
